import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, FontIcon, LoadingSpinner, SvgIcon } from '@gik/ui';
import { Moment } from 'moment';
import moment from 'moment-timezone';
import { useCalendarStore } from '@gik/calendar/store/CalendarStore';
import noop from '@gik/core/utils/noop';
import CalendarDay from './CalendarDay';
import { convertDateFromBackend } from '@gik/core/utils/l10n';
import { ICalendarDay, ICalendarEntry, ICalendarEvent } from '@gik/calendar/models/Calendar';
import CalendarEmptyView from '../EmptyView/CalendarEmptyView';
import { useUserStore } from '@gik/core/store/UserStore';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { CalendarUtil, useCollectRecipientInfo } from '@gik/calendar';
import { useInkind } from '@gik/api/inkinds/inkind';
import { useCalendarEvents } from '@gik/api/calendar/calendar';
import DateTimeUtils, { dateFormat } from '@gik/core/utils/DateTimeUtils';
import { continueClaimAfterLogin } from '@gik/calendar/utils/CalendarModals';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { scrollIntoView } from '@gik/core/utils/scroll';
import { useCalendarEventTypes } from '@gik/api/calendar/calendarEventType';
import { useCalendarAnnouncementTypes } from '@gik/api/calendar/calendarAnnouncementType';
import { usePrevious } from 'react-use';
import { useUser } from '@gik/api/users/user';

export interface ICalendarDaysListProps extends React.HTMLAttributes<HTMLDivElement> {
  selectedMonth?: string;
  scrollToFirstEntry?: boolean;
  onNext?(): void;
  onPrev?(): void;
  onInit?(reload: Function): void;
}

export default function CalendarDaysList({
  selectedMonth,
  className,
  scrollToFirstEntry = true,
  onPrev = noop,
  onNext = noop,
  onInit = noop,
  ...otherProps
}: ICalendarDaysListProps): React.ReactElement {
  const bem = bemBlock('calendar-days-list');
  const { t } = useTranslation();
  const collectRecipientInfo = useCollectRecipientInfo();

  const [scrollTimeout, setScrollTimeout] = React.useState<NodeJS.Timeout>();
  const [prevMonthName, setPrevMonthName] = React.useState<string>();
  const [nextMonthName, setNextMonthName] = React.useState<string>();
  const [firstStartTime, setFirstStartTime] = React.useState<Moment>();
  const [eventsFound, setEventsFound] = React.useState<boolean>();
  const [dataByDays, setDataByDays] = React.useState<ICalendarDay[]>();
  const [reloading, setReloading] = React.useState<boolean>(false);
  const [initialScrollCompleted, setInitialScrollCompleted] = React.useState<boolean>(false);
  const [ignoreFirstScroll, setIgnoreFirstScroll] = React.useState<boolean>(false);

  const mainRef = React.useRef<HTMLElement>();

  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);

  const { data: eventTypes } = useCalendarEventTypes();
  const { data: announcementTypes } = useCalendarAnnouncementTypes();

  const storeSelectedMonth = useCalendarStore(state => state.selectedMonth);
  const setSelectedMonth = useCalendarStore(state => state.setSelectedMonth);
  const setScrollToEntryById = useCalendarStore(state => state.setScrollToEntryById);

  // allow selectedMonth to be provided by props but fallback to the store values
  if (!selectedMonth) {
    selectedMonth = storeSelectedMonth;
  }

  const prevSelectedMonth = usePrevious(selectedMonth);

  const { data: inkind } = useInkind(inkindRouteId);
  // const inkindId = inkind?.id.toString();

  const requestDate = moment.tz(selectedMonth, 'UTC');

  const {
    data: calendarEvents,
    mutate: mutateCalendarEvents,
    error: calendarEventsError,
    isValidating,
  } = useCalendarEvents(
    inkindRouteId,
    requestDate.format('YYYY-MM'),
    requestDate.clone().add(1, 'month').format('YYYY-MM')
  );

  // A ref to the mutate function is needed to be used in the reload function
  const mutateEventsRef = React.useRef<Function>();
  mutateEventsRef.current = mutateCalendarEvents;

  const canEdit = canUserEditPage(inkind, userId);

  // ---------- Functions

  function reload() {
    setReloading(true);
    return mutateEventsRef.current();
  }

  function filterEventsByDay(date: Moment, events: ICalendarEvent[]) {
    return events.filter(function (item) {
      const startDate = convertDateFromBackend(item.startsAt);
      return date.isSame(startDate, 'day');
    });
  }

  function setDate(date: Moment) {
    if (!date) {
      return;
    }
    setSelectedMonth(date.format(DateTimeUtils.MonthFormat));
    setPrevMonthName(date.clone().add(-1, 'month').format('MMM'));
    setNextMonthName(date.clone().add(1, 'month').format('MMM'));
  }

  function scrollToEntryById(entryId: string) {
    const elAppHeader = document.querySelector('.gik-app-header') as HTMLDivElement;
    let offset = -10;
    if (elAppHeader) {
      offset -= elAppHeader.offsetHeight;
    }
    const entryEl = document.querySelector(`[data-id='${entryId}']`) as HTMLDivElement;
    if (entryEl) {
      scrollIntoView(entryEl, { offset, container: mainRef.current });
    }
  }

  async function handleAddEvent(date?: Moment) {
    if (!canEdit) {
      return;
    }
    await collectRecipientInfo();
    CalendarUtil.add(date);
  }

  function handleEditEvent(event: ICalendarEvent, entry: ICalendarEntry) {
    if (!canEdit) {
      return;
    }
    CalendarUtil.edit(event, entry);
  }

  const scrollToCurrentDay = React.useCallback(async () => {
    if (!mainRef || !mainRef.current) {
      return;
    }

    const currentDay = mainRef.current.getElementsByClassName(`gik-calendar-days-list__day-${moment().format('DD')}`);

    if (!ignoreFirstScroll) {
      if (!currentDay || currentDay.length === 0) {
        // just scroll to top if current day can't be found
        if (mainRef.current) {
          mainRef.current.scrollTop = 0;
        }
        return;
      }

      const dayElement = currentDay[0] as HTMLDivElement;
      if (mainRef.current) {
        mainRef.current.scrollTop = dayElement.offsetTop;
      }
      setInitialScrollCompleted(true);
    }
    if (ignoreFirstScroll) {
      setIgnoreFirstScroll(false);
    }
  }, [mainRef, ignoreFirstScroll]);

  const scrollToNextUpcomingEventOrCurrentDay = React.useCallback(() => {
    if (!mainRef || !mainRef.current || !eventsFound) {
      return;
    }

    function delayScroll() {
      clearTimeout(scrollTimeout);
      setScrollTimeout(
        setTimeout(() => {
          scrollToNextUpcomingEventOrCurrentDay();
        }, 300)
      );
    }

    const currentEntriesSelector =
      '.gik-calendar-event:not(.gik-calendar-event--past-event), .gik-calendar-announcement:not(.gik-calendar-announcement--past)';

    const elEntries = mainRef.current.querySelectorAll(currentEntriesSelector);
    const elAppHeader = document.querySelector('.gik-app-header') as HTMLDivElement;
    let offset = -10;
    if (elAppHeader) {
      offset -= elAppHeader.offsetHeight;
    }

    // if calendar events have not been rendered yet to the dom wait a bit and try again later
    const nonEmptyDays = mainRef.current.querySelectorAll(
      `.gik-calendar-day:not(.gik-calendar-day--empty) ${currentEntriesSelector}`
    );

    if (!nonEmptyDays.length) {
      delayScroll();
      return;
    }

    if (!elEntries.length) {
      delayScroll();
      return;
    }

    if (!ignoreFirstScroll) {
      const boxElement = elEntries[0] as HTMLDivElement;
      if (mainRef.current) {
        mainRef.current.scrollTop = boxElement.offsetTop + offset;
      } // need to add the application's header height
      if (!ignoreFirstScroll) {
        setInitialScrollCompleted(true);
      }
      1;
      if (ignoreFirstScroll) {
        setIgnoreFirstScroll(false);
      }
    } else {
      scrollToCurrentDay();
    }
  }, [mainRef, ignoreFirstScroll, scrollToCurrentDay]);

  // ---------- Effects

  /**
   * onMount effect
   */
  React.useEffect(() => {
    setScrollToEntryById(scrollToEntryById);
    onInit(reload);
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (firstStartTime) {
      return;
    }
    if (!calendarEvents || !calendarEvents.events?.length) {
      return;
    }
    // check if the first event occurs this month (lookahead)
    const _firstStartTime = convertDateFromBackend(calendarEvents.events[0].startsAt);
    setFirstStartTime(_firstStartTime);

    if (calendarEvents.events?.length && _firstStartTime.isAfter(requestDate, 'month')) {
      setDate(_firstStartTime);
    } else {
      setDate(requestDate);
    }

    // eslint-disable-next-line
  }, [calendarEvents, firstStartTime]);

  const isRefreshing = isValidating || eventsFound === false || reloading;
  const userCreatedDate = user?.createdDate;
  const inkindPageTitle = inkind?.title;
  const isLoading =
    !calendarEvents || !eventTypes || !announcementTypes || (userId && !userCreatedDate) || !inkindPageTitle;
  /**
   * Parse calendar events when calendar data changes
   */
  React.useEffect(() => {
    if (!calendarEvents || isLoading) {
      return;
    }

    const date = moment.tz(selectedMonth, 'UTC');

    function parseCalendarData(events: ICalendarEvent[]): ICalendarDay[] {
      const totalDays = date.daysInMonth();
      const days: ICalendarDay[] = [];

      let currentDate = date.clone();

      if (!events) {
        return days;
      }

      let _eventsFound = false;

      for (let i = 0; i < totalDays; i++) {
        const dayEvents = filterEventsByDay(currentDate, events);

        days.push({
          date: currentDate.clone().format(dateFormat),
          events: dayEvents.sort((a, b) => {
            return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
          }),
        });
        if (!_eventsFound) {
          _eventsFound = dayEvents.length > 0;
        }
        currentDate = currentDate.add(1, 'day');
      }

      setEventsFound(_eventsFound);

      return days;
    }

    setDataByDays(parseCalendarData(calendarEvents.events));

    // don't scroll if initial scroll completed prevents scrolling from happening when an event is edited.
    if (!initialScrollCompleted) {
      if (scrollToFirstEntry) {
        scrollToNextUpcomingEventOrCurrentDay();
      } else {
        scrollToCurrentDay();
      }
    } else {
      if (selectedMonth !== prevSelectedMonth) {
        scrollToNextUpcomingEventOrCurrentDay();
      }
    }
    continueClaimAfterLogin(calendarEvents, moment.utc(userCreatedDate), inkindPageTitle);
    setReloading(false);
  }, [
    scrollToFirstEntry,
    calendarEvents,
    selectedMonth,
    prevSelectedMonth,
    scrollToCurrentDay,
    scrollToNextUpcomingEventOrCurrentDay,
    initialScrollCompleted,
    isLoading,
    userCreatedDate,
    inkindPageTitle,
  ]);

  React.useEffect(() => {
    // reset initialScrollCompleted if selected month changes
    // if (selectedMonth && mainRef?.current) mainRef.current.scrollTop = 0;
    // if (selectedMonth !== prevSelectedMonth) {
    setInitialScrollCompleted(false);
    // }
  }, [selectedMonth]);

  React.useEffect(() => {
    // if on mount we already have calendarEvents it means it came from the SWR cache
    // we need to wait for initial revalidation before attempting to scroll to an event
    if (calendarEvents) {
      setIgnoreFirstScroll(true);
    }
    //eslint-disable-next-line
  }, []);

  // ---------- Rendering

  // render errors
  if (calendarEventsError) {
    return (
      <div className={bem('error')}>
        <div>
          <FontIcon name="neg-o" />
          <p>{t(translationKeys.calendarFatalError)}</p>
          <Button onClick={() => window.location.reload()}>{t(translationKeys.calendarFatalErrorRefresh)}</Button>
          {/* {fetchError.errorReferenceId && (
            <p className={bem('error-details')}>{`Reference: ${fetchError.errorReferenceId}`}</p>
          )} */}
        </div>
      </div>
    );
  }

  // render loader

  if (isLoading) {
    return (
      <div className={bem('loading')}>
        <LoadingSpinner />
      </div>
    );
  }

  // render empty view is there is data but no events
  if (eventsFound === false && !isValidating) {
    return <CalendarEmptyView onAddEvent={() => handleAddEvent()} canEdit={canUserEditPage(inkind, userId)} />;
  }

  return (
    <div
      className={bem(null, [{ loading: isRefreshing }], className)}
      {...otherProps}
      ref={el => {
        mainRef.current = el;
        return el;
      }}
    >
      {isRefreshing && (
        <div className={bem('loading-overlay')}>
          <LoadingSpinner />
        </div>
      )}
      <ul>
        {dataByDays?.map((day, index) => {
          return (
            <li key={index} className={bem(`day-${moment(day.date).format('DD')}`)}>
              <CalendarDay
                date={convertDateFromBackend(day.date)}
                events={day.events}
                entries={calendarEvents.entries}
                onAddEvent={() => {
                  console.log('day.date', day.date);
                  handleAddEvent(moment(day.date));
                }}
                onEditEvent={handleEditEvent}
                onClaimEvent={CalendarUtil.claim}
              />
            </li>
          );
        })}
      </ul>

      <div className={bem('footer-actions', null, 'no-print')}>
        <Button
          squared
          onClick={onPrev}
          className={bem('btn-prev')}
          prepend={<SvgIcon size="sm" name="ChevronLeftOutline" />}
        >
          {prevMonthName}
        </Button>

        <Button
          squared
          onClick={onNext}
          className={bem('btn-next')}
          append={<SvgIcon size="sm" name="ChevronRightOutline" />}
        >
          {nextMonthName}
        </Button>
      </div>
    </div>
  );
}
